// When the exchange is worth believing about a race, and when it is not.
//
// THE PROBLEM THIS EXISTS FOR. A Betfair WIN market is the best reference price
// in Australian racing once the money has arrived, and it is nothing at all
// before that: most AU races fill inside the last four minutes, so at half past
// noon a 2:40 race can be a handful of dollars parked at long odds by two
// people, and the app was reading that as "the price the money settled on". A
// runner measured against a market nobody has bet into is a runner measured
// against nothing, printed in the action ink.
//
// SO THE REFERENCE IS GATED, on the only two facts that say whether the money
// is in yet:
//
//   TRADED VOLUME — `totalMatched` on the market, which the Betfair Betting API
//   returns when `EX_TRADED` is asked for and the app key is a LIVE one.
//   MINUTES TO THE JUMP — the clock, which every device has.
//
// AND THE LIVE KEY MAY NEVER ARRIVE. The owner has a DELAYED app key: it serves
// prices and returns no volume at all, and the live key is an Australian webform
// nobody is promising. So volume is an OPTION here, not a requirement: with it,
// a floor per code decides; without it, the clock alone decides and the screen
// says so in different words ("exchange, near the jump"). Nothing in this
// module, and nothing that reads it, may assume a number that a delayed key
// cannot produce.
//
// PURE, AND A LEAF. It imports nothing: a couple of numbers go in and an answer
// comes out, so every branch — including the no-volume one, which is the branch
// the app actually runs in today — is pinned in the suite rather than argued
// about against a live market.

/**
 * How much money has to have gone through a market before its price is read as
 * the market's opinion, by code.
 *
 * THESE ARE PRIORS, NOT FINDINGS, and the difference matters. They are set from
 * what a typical AU market turns over by the jump — a metro Saturday
 * thoroughbred win market is tens of thousands of dollars matched, a midweek
 * country race single figures of thousands, and a greyhound or harness race
 * less again — and they are deliberately conservative: too high and the app
 * falls back to the local books, which is the answer it gave before this gate
 * existed; too low and it trusts a market two people have traded.
 *
 * THEY ARE TO BE FITTED FROM THE R7 HARNESS. The racing history carries
 * `WIN_BSP_VOLUME` per runner per race for six years, so the honest version of
 * this table is the volume at which the exchange's own starting price starts
 * beating the local consensus at predicting the winner. That is a backtest
 * nobody has run yet, and until somebody does, a number with its reasoning
 * written down beats a number with a decimal point on it.
 */
export const VOLUME_FLOORS = Object.freeze({
  'thoroughbred-metro': 20000,
  'thoroughbred-country': 8000,
  greyhound: 5000,
  harness: 5000,
});

/** The floor for a code this module does not know: the tightest of them. */
export const DEFAULT_VOLUME_FLOOR = VOLUME_FLOORS['thoroughbred-metro'];

/**
 * How close to the jump a market has to be, with the money in it, before it is
 * the reference. Five minutes is the window most AU races fill inside.
 */
export const TRUSTED_MINUTES = 5;

/**
 * How close to the jump a market has to be when there is NO volume to read.
 *
 * Tighter than the volume gate, and tighter on purpose: three minutes out is
 * the strongest claim the clock alone can make, and the clock alone is all a
 * delayed key ever gives. It is a proxy for "the money is in by now" rather
 * than a measurement of it, and the label the screens print says which of the
 * two the figure rests on.
 */
export const NO_VOLUME_MINUTES = 3;

/** The eight metropolitan thoroughbred tracks, by the slug a matchKey carries. */
export const METRO_VENUES = Object.freeze([
  'randwick', 'rosehill', 'warwick-farm', 'canterbury',
  'flemington', 'caulfield', 'moonee-valley', 'sandown-hillside', 'sandown-lakeside',
  'eagle-farm', 'doomben', 'morphettville', 'ascot', 'belmont',
]);

/** Is this venue slug one of the metro tracks? */
export const isMetro = (venueId) => METRO_VENUES.includes(String(venueId ?? '').toLowerCase());

/**
 * Which floor a race is held to: the code, and for the gallops whether it is a
 * metro meeting or a country one.
 *
 * `racing_horse` covers both the gallops and the trots on the live card (the
 * exchange cannot tell them apart; see match-exchange.js), so harness is read
 * off the sport id where the app knows it and falls back to the gallops
 * otherwise.
 */
export function codeFor(sport, venueId = null) {
  if (sport === 'racing_greyhound') return 'greyhound';
  if (sport === 'racing_harness') return 'harness';
  return isMetro(venueId) ? 'thoroughbred-metro' : 'thoroughbred-country';
}

/** The venue slug out of a race's `matchKey`, or null. */
export function venueOfKey(matchKey) {
  const parts = String(matchKey ?? '').split(':');
  return parts.length >= 4 ? parts[2] : null;
}

const finite = (n) => {
  if (n === null || n === undefined || n === '') return null;
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
};

/**
 * Is the exchange worth believing about this market right now?
 *
 *   { trusted, reason, basis, floor, label }
 *
 *   trusted  use the exchange's own price as the fair one.
 *   reason   why not, when it is not: 'thin' (money is in the market and there
 *            is not enough of it), 'early' (there is enough but the race is a
 *            way off), 'no-volume' (this key cannot see volume and the race is
 *            not close enough for the clock to stand in).
 *   basis    what the answer rests on: 'volume', 'time', or null.
 *   floor    the dollars this code is held to, for a screen that wants to say.
 *   label    the four words a chip prints.
 *
 * `minutesToJump` unknown is treated as "a long way off": a race with no
 * readable start is not a race the app can say the money has arrived on.
 */
export function exchangeTrust({ totalMatched = null, minutesToJump = null, code = null } = {}) {
  const floor = VOLUME_FLOORS[code] ?? DEFAULT_VOLUME_FLOOR;
  const matched = finite(totalMatched);
  const minutes = finite(minutesToJump) ?? Number.POSITIVE_INFINITY;

  // NO VOLUME AT ALL: the delayed key, which is the key this app runs on today.
  // The clock is the whole of the evidence, and the label says so.
  if (matched === null) {
    return minutes <= NO_VOLUME_MINUTES
      ? { trusted: true, reason: null, basis: 'time', floor, label: 'exchange, near the jump' }
      : { trusted: false, reason: 'no-volume', basis: null, floor, label: 'local books' };
  }

  if (matched < floor) return { trusted: false, reason: 'thin', basis: null, floor, label: 'local books' };
  if (minutes > TRUSTED_MINUTES) return { trusted: false, reason: 'early', basis: null, floor, label: 'local books' };
  return { trusted: true, reason: null, basis: 'volume', floor, label: 'exchange' };
}

/** Dollars with no cents on them: "$20,000". Money in the middle of a sentence. */
const dollars = (n) => `$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;

/** "4 minutes", "1 minute", and "a way out" for a race with no readable clock. */
function minutesPhrase(minutesToJump) {
  const m = finite(minutesToJump);
  if (m === null) return 'a way out';
  if (m <= 0) return 'at the jump';
  const n = Math.round(m);
  return `${n} minute${n === 1 ? '' : 's'} out`;
}

/**
 * The one sentence under a race about which yardstick its figures are measured
 * against, and why.
 *
 * It says the fact rather than a warning: a race priced against the local books
 * is the ordinary state of this screen for most of the day, and nothing is
 * wrong when it is. `chance`, `fair` and `margin` are the app's words; "edge"
 * belongs to a leg, not to a reference price.
 */
export function trustLine(trust, { totalMatched = null, minutesToJump = null } = {}) {
  if (!trust) return '';
  const when = minutesPhrase(minutesToJump);
  if (trust.trusted && trust.basis === 'volume') {
    return `Fair is the Betfair exchange, net of commission: ${dollars(totalMatched)} matched, ${when}.`;
  }
  if (trust.trusted) {
    return `Fair is the Betfair exchange, net of commission, ${when}. This key carries prices and no volume, so the clock is the whole of the reason.`;
  }
  if (trust.reason === 'thin') {
    return `Fair is the local books: only ${dollars(totalMatched)} has been matched on the exchange, under the ${dollars(trust.floor)} this code is held to.`;
  }
  if (trust.reason === 'early') {
    return `Fair is the local books: the exchange fills in the last few minutes and this race is ${when}.`;
  }
  return `Fair is the local books: this Betfair key carries prices and no traded volume, and the race is ${when}.`;
}
